import { useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Dumbbell } from "lucide-react"
import type { Appointment } from "../../../types"
import type { CalendarRoutine } from "../../../api/appointments"
import { routinesOnDay } from "../../../utils/routineSchedule"
import AppointmentCard from "./AppointmentCard"

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]

function getMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1)
  const last = new Date(year, month + 1, 0)
  // Monday = 0 for our grid
  let startDay = first.getDay() - 1
  if (startDay < 0) startDay = 6

  const days: (Date | null)[] = []
  for (let i = 0; i < startDay; i++) days.push(null)
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d))
  while (days.length % 7 !== 0) days.push(null)
  return days
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function getRoutinesForDay(routines: CalendarRoutine[], day: Date): CalendarRoutine[] {
  return routinesOnDay(routines, day)
}

export default function MonthView({
  year,
  month,
  appointments,
  routines = [],
  onSelectAppointment,
  onDropAppointment,
}: {
  year: number
  month: number
  appointments: Appointment[]
  routines?: CalendarRoutine[]
  onSelectAppointment: (a: Appointment) => void
  onDropAppointment?: (appointmentId: number, targetDate: Date) => void
}) {
  const days = getMonthGrid(year, month)
  const today = new Date()
  const [dragOverDate, setDragOverDate] = useState<string | null>(null)

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  function handleDrop(e: React.DragEvent, targetDate: Date) {
    e.preventDefault()
    setDragOverDate(null)
    const idStr = e.dataTransfer.getData("application/appointment-id")
    if (idStr && onDropAppointment) {
      onDropAppointment(Number(idStr), targetDate)
    }
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${year}-${month}`}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -30 }}
        transition={{ duration: 0.2 }}
      >
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-xs font-medium text-muted py-2">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, i) => {
            if (!day) {
              return <div key={`empty-${i}`} className="min-h-[100px] rounded-lg bg-white/[0.02]" />
            }

            const isToday = isSameDay(day, today)
            const dayKey = day.toISOString()
            const isDragOver = dragOverDate === dayKey
            const dayAppts = appointments.filter((a) =>
              isSameDay(new Date(a.start_time), day)
            )
            const dayRoutines = getRoutinesForDay(routines, day)

            // Group routines by hour for compact display
            const routinesByHour = new Map<number, CalendarRoutine[]>()
            dayRoutines.forEach((r) => {
              const existing = routinesByHour.get(r.start_hour) || []
              existing.push(r)
              routinesByHour.set(r.start_hour, existing)
            })

            const totalItems = dayAppts.length + routinesByHour.size
            const maxVisible = 3

            return (
              <div
                key={dayKey}
                onDragOver={handleDragOver}
                onDragEnter={() => setDragOverDate(dayKey)}
                onDragLeave={() => setDragOverDate((prev) => prev === dayKey ? null : prev)}
                onDrop={(e) => handleDrop(e, day)}
                className={`min-h-[100px] rounded-lg border p-1.5 transition-colors ${
                  isDragOver
                    ? "border-primary/60 bg-primary/10"
                    : isToday
                      ? "border-primary/40 bg-primary/5"
                      : "border-white/5 bg-white/[0.02] hover:border-white/10"
                }`}
              >
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                    isToday ? "bg-primary text-black font-bold" : "text-muted"
                  }`}
                >
                  {day.getDate()}
                </span>
                <div className="mt-1 space-y-1">
                  {dayAppts.slice(0, maxVisible).map((appt) => (
                    <AppointmentCard
                      key={appt.id}
                      appointment={appt}
                      compact
                      onClick={() => onSelectAppointment(appt)}
                    />
                  ))}
                  {/* Routine chips */}
                  {Array.from(routinesByHour.entries())
                    .slice(0, Math.max(0, maxVisible - dayAppts.length))
                    .map(([hour, rList]) => (
                      <div
                        key={`r-${hour}`}
                        className="w-full rounded-lg border border-violet-400/40 bg-violet-400/10 p-1.5"
                      >
                        <div className="flex items-start gap-1">
                          <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-violet-400" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold text-violet-400 truncate flex items-center gap-0.5">
                              <Dumbbell size={8} />
                              {`${String(hour).padStart(2, "0")}:00`} · {rList.map((r) => r.user?.name?.split(" ")[0]).join(", ")}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  {totalItems > maxVisible && (
                    <p className="text-[10px] text-muted text-center">
                      +{totalItems - maxVisible} más
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
